import React, { useEffect, useMemo, useState } from 'react';
import Layout from '../../components/Layout';
import ApiInspector from '../../components/ApiInspector';
import { RowIcon } from '../../components/BopRowActions';
import { showToast } from '../../components/Toast';
import { apiFetch } from '../../lib/api';
import { useFiltroSocieta } from '../../hooks/useFiltroSocieta';
import { usePermesso } from '../../context/PermessiContext';
import { useDirtyForm } from '../../hooks/useDirtyForm';
import { formatCurrency } from '../../utils/formatters';
import { API_DOCS } from './CaricamentoMassivoMovimentiPage.api';
import './CaricamentoMassivoMovimentiPage.css';

const MODULO = 'controllo_gestione.movimenti_massivi';

type Natura = 'COSTO' | 'RICAVO';

interface ContoNode {
  id: number;
  codice: string;
  descrizione: string;
  natura: Natura;
  attivo: boolean;
  livello: number;
  divisioneId: number | null;
  figli: ContoNode[];
}

interface Divisione { id: number; codice: string; descrizione: string; attivo: boolean; }

const LIVELLO_LABEL: Record<number, string> = { 1: 'Centro', 2: 'Conto', 3: 'Sottoconto' };

const oggiISO = () => new Date().toISOString().slice(0, 10);

const parseImporto = (s: string | undefined): number | null => {
  if (s == null) return null;
  const t = s.trim();
  if (t === '') return null;
  const n = Number(t.replace(',', '.'));
  if (!Number.isFinite(n) || n === 0) return null;
  return n;
};

export default function CaricamentoMassivoMovimentiPage() {
  const { can } = usePermesso();
  const canEdit = can(MODULO, 'edit');

  const { societaId, setSocietaId, listaSocieta, isUnica } = useFiltroSocieta();

  const [data, setData] = useState<string>(oggiISO());
  const [descrizione, setDescrizione] = useState('');
  const [competenzaDal, setCompetenzaDal] = useState('');
  const [competenzaAl, setCompetenzaAl] = useState('');

  const [tree, setTree] = useState<ContoNode[]>([]);
  const [divisioni, setDivisioni] = useState<Divisione[]>([]);
  const [filtroDivisioneId, setFiltroDivisioneId] = useState('');
  const [edits, setEdits] = useState<Record<number, string>>({});
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const collectExpandable = (nodes: ContoNode[], acc: Set<number>) => {
    for (const n of nodes) {
      if (n.figli.length) { acc.add(n.id); collectExpandable(n.figli, acc); }
    }
  };

  const load = async () => {
    if (!societaId) { setTree([]); setDivisioni([]); setEdits({}); setLoading(false); return; }
    setLoading(true);
    setError('');
    try {
      // @api GET /api/conti-analitici?societaId= -> { success: true, data: [{ id, codice, descrizione, natura, attivo, livello, divisioneId, figli }] }
      const res = await apiFetch<{ success: boolean; data: ContoNode[] }>(
        `/api/conti-analitici?societaId=${societaId}`,
      );
      // @api GET /api/divisioni-analitiche?societaId= -> { success: true, data: [{ id, codice, descrizione, attivo }] }
      const resDiv = await apiFetch<{ success: boolean; data: Divisione[] }>(
        `/api/divisioni-analitiche?societaId=${societaId}`,
      );
      const dataTree = res.data ?? [];
      setTree(dataTree);
      setDivisioni(resDiv.data ?? []);
      setEdits({});
      const exp = new Set<number>();
      collectExpandable(dataTree, exp);
      setExpanded(exp);
    } catch (e: any) {
      setError(e.message || 'Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [societaId]);

  const toggle = (id: number) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const setImporto = (contoId: number, value: string) => {
    setEdits(prev => ({ ...prev, [contoId]: value }));
  };

  const aggregato = (node: ContoNode): number => {
    if (node.figli.length === 0) return node.attivo ? (parseImporto(edits[node.id]) ?? 0) : 0;
    return node.figli.reduce((sum, f) => sum + aggregato(f), 0);
  };

  // Conteggio voci valorizzate + totali per natura
  const riepilogo = useMemo(() => {
    let count = 0;
    let costi = 0;
    let ricavi = 0;
    const walk = (nodes: ContoNode[]) => {
      for (const n of nodes) {
        if (n.figli.length === 0) {
          if (n.attivo) {
            const v = parseImporto(edits[n.id]);
            if (v !== null) {
              count++;
              if (n.natura === 'COSTO') costi += v; else ricavi += v;
            }
          }
        } else {
          walk(n.figli);
        }
      }
    };
    walk(tree);
    return { count, costi, ricavi };
  }, [tree, edits]);

  const isDirty = riepilogo.count > 0;
  useDirtyForm(isDirty, 'Caricamento massivo movimenti');

  const genera = async () => {
    if (!societaId) return;
    if (!data) { setError('Data obbligatoria'); return; }
    if (!descrizione.trim()) { setError('Descrizione obbligatoria'); return; }
    if (riepilogo.count === 0) { setError('Inserisci almeno un importo diverso da zero'); return; }
    if ((competenzaDal && !competenzaAl) || (!competenzaDal && competenzaAl)) {
      setError('Indica entrambe le date di competenza oppure nessuna');
      return;
    }
    if (competenzaDal && competenzaAl && new Date(competenzaAl) < new Date(competenzaDal)) {
      setError('La competenza "al" deve essere >= "dal"');
      return;
    }

    const voci: { contoAnaliticoId: number; importo: number }[] = [];
    for (const k of Object.keys(edits)) {
      const id = Number(k);
      const v = parseImporto(edits[id]);
      if (v !== null) voci.push({ contoAnaliticoId: id, importo: v });
    }

    setSaving(true);
    setError('');
    try {
      // @api POST /api/movimenti-conto-analitico/bulk { societaId, data, descrizione, competenzaDal, competenzaAl, voci: [{ contoAnaliticoId, importo }] } -> { success: true, data: { creati } }
      const res = await apiFetch<{ success: boolean; data: { creati: number } }>(
        '/api/movimenti-conto-analitico/bulk',
        {
          method: 'POST',
          body: JSON.stringify({
            societaId: Number(societaId),
            data,
            descrizione: descrizione.trim(),
            competenzaDal: competenzaDal || null,
            competenzaAl: competenzaAl || null,
            voci,
          }),
        },
      );
      showToast(`Creati ${res.data?.creati ?? voci.length} movimenti`, 'success');
      setEdits({}); // svuota la griglia, mantiene l'intestazione
    } catch (e: any) {
      setError(e.message || 'Errore nella generazione dei movimenti');
    } finally {
      setSaving(false);
    }
  };

  const renderRows = (nodes: ContoNode[]): React.ReactNode[] => {
    const out: React.ReactNode[] = [];
    for (const n of nodes) {
      if (!n.attivo) continue;
      const isLeaf = n.figli.length === 0;
      const isOpen = expanded.has(n.id);
      out.push(
        <tr key={n.id} className={isLeaf ? '' : 'cmm-row--parent'}>
          <td>
            <div className={`cmm-codice-cell cmm-codice-cell--liv${n.livello}`}>
              {!isLeaf ? (
                <button
                  type="button"
                  className={`cmm-toggle ${isOpen ? 'cmm-toggle--open' : ''}`}
                  onClick={() => toggle(n.id)}
                  aria-label={isOpen ? 'Comprimi' : 'Espandi'}
                >
                  <RowIcon.Chevron />
                </button>
              ) : (
                <span className="cmm-toggle-spacer" />
              )}
              <span className="cmm-codice">{n.codice}</span>
            </div>
          </td>
          <td>{n.descrizione} <span className="cmm-livello-tag">{LIVELLO_LABEL[n.livello]}</span></td>
          <td>
            <span className={`bop-badge ${n.natura === 'RICAVO' ? 'bop-badge--success' : 'bop-badge--warning'}`}>
              {n.natura === 'RICAVO' ? 'Ricavo' : 'Costo'}
            </span>
          </td>
          <td className="bop-text-right">
            {isLeaf ? (
              <input
                className="bop-input cmm-importo-input"
                type="number"
                step="0.01"
                placeholder="0,00"
                value={edits[n.id] ?? ''}
                onChange={e => setImporto(n.id, e.target.value)}
                disabled={!canEdit}
              />
            ) : (
              <span className="cmm-aggregato">{formatCurrency(aggregato(n))}</span>
            )}
          </td>
        </tr>,
      );
      if (!isLeaf && isOpen) out.push(...renderRows(n.figli));
    }
    return out;
  };

  const noSocieta = !societaId;
  // Albero filtrato per divisione (se selezionata): mostra solo i Centri (livello 1)
  // della divisione e i loro discendenti.
  const treeFiltrato = useMemo(() => {
    if (!filtroDivisioneId) return tree;
    const did = Number(filtroDivisioneId);
    return tree.filter(c => c.divisioneId === did);
  }, [tree, filtroDivisioneId]);
  const rows = renderRows(treeFiltrato);

  return (
    <Layout>
      <div className="caricamento-massivo-movimenti">
        <div className="bop-page-header">
          <div className="bop-page-header__text">
            <h2>Caricamento massivo movimenti</h2>
            <p>Genera più movimenti in un colpo: una data e una descrizione comuni, un importo per conto</p>
          </div>
          {canEdit && (
            <button
              className="bop-btn bop-btn-primary"
              onClick={genera}
              disabled={noSocieta || saving || riepilogo.count === 0}
            >
              {saving ? 'Genero…' : `Genera movimenti${riepilogo.count ? ` (${riepilogo.count})` : ''}`}
            </button>
          )}
        </div>

        {(!isUnica || divisioni.length > 0) && (
          <div className="bop-filter-bar">
            {!isUnica && (
              <select className="bop-select" value={societaId} onChange={e => setSocietaId(e.target.value)}>
                <option value="">Seleziona società…</option>
                {listaSocieta.map(s => <option key={s.id} value={s.id}>{s.nome}</option>)}
              </select>
            )}
            {!noSocieta && divisioni.length > 0 && (
              <select className="bop-select" value={filtroDivisioneId} onChange={e => setFiltroDivisioneId(e.target.value)}>
                <option value="">Tutte le divisioni</option>
                {divisioni.map(d => <option key={d.id} value={d.id}>{d.codice} — {d.descrizione}</option>)}
              </select>
            )}
          </div>
        )}

        {!noSocieta && (
          <div className="bop-card cmm-header-card">
            <div className="bop-card__body">
              <div className="bop-form-row cmm-header-row">
                <div className="bop-form-group">
                  <label className="bop-label">Data *</label>
                  <input className="bop-input" type="date" value={data} onChange={e => setData(e.target.value)} />
                </div>
                <div className="bop-form-group cmm-descrizione-group">
                  <label className="bop-label">Descrizione (comune a tutti) *</label>
                  <input
                    className="bop-input"
                    placeholder="es. Ribaltamento costi struttura aprile"
                    value={descrizione}
                    onChange={e => setDescrizione(e.target.value)}
                  />
                </div>
                <div className="bop-form-group">
                  <label className="bop-label">Competenza dal</label>
                  <input className="bop-input" type="date" value={competenzaDal} onChange={e => setCompetenzaDal(e.target.value)} />
                </div>
                <div className="bop-form-group">
                  <label className="bop-label">Competenza al</label>
                  <input className="bop-input" type="date" value={competenzaAl} onChange={e => setCompetenzaAl(e.target.value)} />
                </div>
              </div>
            </div>
          </div>
        )}

        {error && <div className="bop-inline-error">{error}</div>}

        <div className="bop-card">
          <div className="bop-table-wrapper">
            {noSocieta ? (
              <div className="bop-empty-state">Seleziona una società per caricare i movimenti.</div>
            ) : loading ? (
              <div className="bop-empty-state">Caricamento…</div>
            ) : rows.length === 0 ? (
              <div className="bop-empty-state">Nessun conto attivo. Crea prima il piano dei conti analitico.</div>
            ) : (
              <table className="bop-table">
                <thead>
                  <tr>
                    <th>Codice</th>
                    <th>Descrizione</th>
                    <th>Natura</th>
                    <th className="bop-text-right">Importo (€)</th>
                  </tr>
                </thead>
                <tbody>{rows}</tbody>
                <tfoot>
                  <tr>
                    <td colSpan={3} className="bop-text-right"><strong>Totale costi</strong></td>
                    <td className="bop-text-right"><strong>{formatCurrency(riepilogo.costi)}</strong></td>
                  </tr>
                  <tr>
                    <td colSpan={3} className="bop-text-right"><strong>Totale ricavi</strong></td>
                    <td className="bop-text-right"><strong>{formatCurrency(riepilogo.ricavi)}</strong></td>
                  </tr>
                </tfoot>
              </table>
            )}
          </div>
        </div>
      </div>

      <ApiInspector docs={API_DOCS} />
    </Layout>
  );
}
